/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useRef, useState } from 'react';

import { ASDRatchets, InputDefinitions } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import infoTokens from '@amzn/meridian-tokens/base/icon/info';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';

import { isNil } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import {
    getAsdRatchetsCalStrategy,
    getAsdRatchetsDenominator,
    getAsdRatchetsDescription,
    getAsdRatchetsNumerator
} from 'src/components/RateCardDetail/ContractTerms/AsdRatchets/asdRatchetsUtil';
import { RateCardCoreRateGridColumnName } from 'src/constants';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { IS_REQUIRED } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { IContractTermsComponentProps } from 'src/types/IRateCard';
import { hasErrorMessage } from 'src/util/validationsUtil';

import './AsdRatchets.scss';
import { asdInputDefinitionsThresholdsMap, asdModels } from './asdRatchetThresholds';

export const AsdRatchets = ({
    contractTerms,
    handleContractTermValueChange,
    setIsContractTermInputValid,
    coreRate,
    isEditMode,
    handleContractTermErrorMessageChange,
    contractTermErrorMessages,
    optionalSection,
    setOptionalSection
}: IContractTermsComponentProps) => {
    const { setTopAlertMsg } = React.useContext(AppContext);
    const [isOpen, setIsOpen] = useState(false);
    const [isActiveTier2, setIsActiveTier2] = useState(false);
    const [isActiveTier3, setIsActiveTier3] = useState(false);
    const [inputDefinitionList, setInputDefinitionList] = useState<InputDefinitions[]>([]);
    const [filteredInputDefinitionList, setFilteredInputDefinitionList] = useState<InputDefinitions[]>([]);
    const [selectedAsdModel, setSelectedAsdModel] = useState<string>('');
    const buttonRef = useRef();

    useEffect(() => {
        if (isEditMode) {
            contractConfigurationClient
                .getASDRatchetsInputDefinitions()
                .toPromise()
                .then((response) => {
                    if (response.inputDefinitionsList) {
                        setInputDefinitionList(response.inputDefinitionsList);
                        setFilteredInputDefinitionList(response.inputDefinitionsList);
                    }
                })
                .catch((error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error(error.receivedException?.message);
                    setTopAlertMsg(error.receivedException?.message);
                });
        }
    }, []);

    useEffect(() => {
        const clonedOptionalSection = optionalSection!.filter((e) => !['asdRatchets', 'tier3Threshold'].includes(e));
        const isTier2Active =
            coreRate?.length !== 0 &&
            coreRate?.some(
                (rate) =>
                    rate[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== undefined &&
                    rate[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== '' &&
                    rate[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== null
            );
        const isTier3Active =
            coreRate?.length !== 0 &&
            coreRate?.some(
                (rate) =>
                    rate[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== undefined &&
                    rate[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== '' &&
                    rate[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== null
            );
        if (isTier2Active) {
            setIsActiveTier2(isTier2Active);
        } else {
            clonedOptionalSection.push('asdRatchets');
        }
        if (isTier3Active) {
            setIsActiveTier3(isTier3Active);
        } else {
            clonedOptionalSection.push('tier3Threshold');
        }
        if (setOptionalSection) {
            setOptionalSection(clonedOptionalSection);
        }
    }, [coreRate]);

    const handleSingleErrorMsgChange = (field: string, value: string) => {
        if (!handleContractTermErrorMessageChange) return;
        handleContractTermErrorMessageChange('asdRatchets', field, value);
    };

    useEffect(() => {
        if (!setIsContractTermInputValid) return;
        setIsContractTermInputValid((prevState) => ({
            ...prevState,
            asdRatchets: !hasErrorMessage(contractTermErrorMessages!.asdRatchets)
        }));
    }, [contractTermErrorMessages!.asdRatchets]);

    // Filter input definitions based on ASD model
    useEffect(() => {
        const filteredInputDefinitionsByAsdModel = inputDefinitionList.filter((inputDefinition) =>
            asdModels[selectedAsdModel].includes(inputDefinition.description!)
        );
        setFilteredInputDefinitionList(filteredInputDefinitionsByAsdModel);
    }, [selectedAsdModel]);

    // Set ASD model if input definition given
    useEffect(() => {
        const inputDefinitionsDescription = contractTerms.asdRatchets?.inputDefinitions?.description;
        if (isEditMode && inputDefinitionsDescription) {
            let derivedAsdModel = '';
            for (const key in asdModels) {
                if (asdModels[key].includes(inputDefinitionsDescription)) {
                    derivedAsdModel = key;
                    break;
                }
            }
            setSelectedAsdModel(derivedAsdModel);
        }
    }, []);

    const handleAsdModelChange = (value: string) => {
        setSelectedAsdModel(value);

        // Reset ASD fields
        const inputDefinition: InputDefinitions = {
            calcStrategy: undefined,
            description: undefined,
            denominator: undefined,
            numerator: undefined,
            planWeight: undefined,
            ratchetModelId: undefined
        };
        const newAsdRatchets: ASDRatchets = {
            inputDefinitions: inputDefinition,
            thresholdOperator: undefined,
            tier2Threshold: undefined,
            tier3Threshold: undefined
        };
        handleContractTermValueChange('asdRatchets', '', newAsdRatchets);
        handleSingleErrorMsgChange('inputDefinitions', IS_REQUIRED);
    };

    const handleInputDefinitionChange = (value: string) => {
        const inputDefinitions = inputDefinitionList.find((inputDefinition) => inputDefinition.description === value);
        const newAsdRatchets: ASDRatchets = {
            inputDefinitions,
            ...asdInputDefinitionsThresholdsMap[inputDefinitions!.description!]
        };

        if (validateMissingRateOrThreshold(newAsdRatchets)) {
            return;
        }

        handleContractTermValueChange('asdRatchets', '', newAsdRatchets);
        handleSingleErrorMsgChange('inputDefinitions', '');
    };

    const validateMissingRateOrThreshold = (ratchet: ASDRatchets) => {
        if (isActiveTier2 && isActiveTier3) {
            if (isNil(ratchet.tier2Threshold) || isNil(ratchet.tier3Threshold)) {
                handleSingleErrorMsgChange(
                    'inputDefinitions',
                    'Must select input definition with both Tier 2 and Tier 3 thresholds'
                );

                return true;
            }
        } else if (isActiveTier2 && !isActiveTier3) {
            if (!isNil(ratchet.tier3Threshold)) {
                handleSingleErrorMsgChange('inputDefinitions', 'Must select input definition without Tier 3 threshold');

                return true;
            }
        }

        return false;
    };

    return (
        <>
            {isActiveTier2 && (
                <Box className="box-border-light-gray" type="outline" spacingInset="400">
                    <Column spacing="500">
                        <Row spacing="none">
                            <Heading level={3}>ASD ratchets</Heading>
                            <ContractTermInfoButton contractTermName={ContractTermName.ASDRatchets} />
                        </Row>
                        {isEditMode && (
                            <Select
                                placeholder="ASD model *"
                                size="xlarge"
                                width="60%"
                                value={selectedAsdModel}
                                onChange={handleAsdModelChange}
                                data-testid="asd-model-select"
                            >
                                {Object.keys(asdModels).map((asdModel) => (
                                    <SelectOption label={asdModel} value={asdModel} key={asdModel} />
                                ))}
                            </Select>
                        )}
                        <Row widths="50%" alignmentVertical="top">
                            <Column spacing="100">
                                <Text type="b200">Rolling monthly average</Text>
                                <Text type="b300">{contractTerms.asdRatchets!.rollingMonthlyAverage ?? 'n/a'}</Text>
                            </Column>
                            <Column spacing="100">
                                {!isEditMode && <Text type="b200">Input definitions</Text>}
                                <Row widths={['grid-11', 'grid-1']}>
                                    {isEditMode ? (
                                        <Select
                                            placeholder="Input definitions *"
                                            size="xlarge"
                                            value={getAsdRatchetsDescription(contractTerms.asdRatchets)}
                                            onChange={(newVal) => handleInputDefinitionChange(newVal)}
                                            error={!!contractTermErrorMessages!.asdRatchets.inputDefinitions}
                                            errorMessage={contractTermErrorMessages!.asdRatchets.inputDefinitions}
                                            disabled={selectedAsdModel === ''}
                                            data-testid="input-definitions"
                                        >
                                            {filteredInputDefinitionList.map((inputDefinition) => (
                                                <SelectOption
                                                    label={inputDefinition.description || ''}
                                                    value={inputDefinition.description || ''}
                                                    key={inputDefinition.description || ''}
                                                />
                                            ))}
                                        </Select>
                                    ) : (
                                        <Text type="b300">{getAsdRatchetsDescription(contractTerms.asdRatchets)}</Text>
                                    )}
                                    <>
                                        <Button
                                            type="link"
                                            size="small"
                                            ref={buttonRef}
                                            onClick={() => setIsOpen(true)}
                                        >
                                            <Icon tokens={infoTokens} />
                                        </Button>
                                        <Popover
                                            anchorNode={buttonRef.current}
                                            open={isOpen}
                                            onClose={() => setIsOpen(false)}
                                            position="end"
                                        >
                                            <PopoverHeader closeLabel="Close">
                                                <Text type="b200" className="input-definitions-header">
                                                    Input definitions
                                                </Text>
                                            </PopoverHeader>
                                            <Column>
                                                <Row alignmentHorizontal="justify">
                                                    <Text type="b200" className="input-definition-label">
                                                        Calc strategy
                                                    </Text>
                                                    <Text type="b200">
                                                        {getAsdRatchetsCalStrategy(contractTerms.asdRatchets)}
                                                    </Text>
                                                </Row>
                                                <Row alignmentHorizontal="justify">
                                                    <Text type="b200" className="input-definition-label">
                                                        Numerator
                                                    </Text>
                                                    <Text type="b200">
                                                        {getAsdRatchetsNumerator(contractTerms.asdRatchets)}
                                                    </Text>
                                                </Row>
                                                <Row alignmentHorizontal="justify">
                                                    <Text type="b200" className="input-definition-label">
                                                        Denominator
                                                    </Text>
                                                    <Text type="b200">
                                                        {getAsdRatchetsDenominator(contractTerms.asdRatchets)}
                                                    </Text>
                                                </Row>
                                                <Row alignmentHorizontal="justify">
                                                    <Text type="b200" className="input-definition-label">
                                                        Plan weight
                                                    </Text>
                                                    <Text type="b200">See Plan Weights Screen</Text>
                                                </Row>
                                            </Column>
                                        </Popover>
                                    </>
                                </Row>
                            </Column>
                        </Row>
                        <Row widths="50%">
                            <Column spacing="500">
                                <Column spacing="100">
                                    <Text type="b200">Tier 2 threshold</Text>
                                    <Text type="b300">
                                        {contractTerms.asdRatchets!.tier2Threshold
                                            ? `${contractTerms.asdRatchets!.thresholdOperator!}
                                                    ${contractTerms.asdRatchets!.tier2Threshold}`
                                            : 'n/a'}
                                    </Text>
                                </Column>
                                <Column spacing="100">
                                    <Text type="b200">Tier 3 threshold</Text>
                                    <Text type="b300">
                                        {contractTerms.asdRatchets!.tier3Threshold
                                            ? `${contractTerms.asdRatchets!.thresholdOperator!}
                                                    ${contractTerms.asdRatchets!.tier3Threshold}`
                                            : 'n/a'}
                                    </Text>
                                </Column>
                            </Column>
                        </Row>
                    </Column>
                </Box>
            )}
        </>
    );
};
